import { Helmet } from "react-helmet"
import { CertTitle } from "../CertTitle"
import { Card, Spinner } from "react-bootstrap"
import { CheckCircle, ExclamationDiamond, XCircle } from "react-bootstrap-icons"
import { useDispatch, useSelector } from "react-redux"
import { useEffect, useState } from "react"
import { setOrderInStore } from "../../../features/orderTPSlice"
import { IOrder } from "../../../interface/IOrder"
import { useGetOrderMutation } from "../../../features/CallTPApi"
import { useTranslation } from "react-i18next"


const StatusMessage = ({
    icon: Icon,
    color,
    title,
    description,
    subDescription,
    isLoading = false,
    order
}: {
    icon: any;
    color: string;
    title: string;
    description?: string;
    subDescription?: string;
    isLoading?: boolean;
    order: IOrder
}) => (

    <Card.Body className="pt-5 text-center">
        <div className={`mb-3 ${color}`}>
            <Icon size={60} />
        </div>
        <h3>{title}</h3>
        {!isLoading && <h4 className="mb-3">Ваш заказ № {order.orderNumber}</h4>}
        {description && <p className="fs-5 mb-4">{description}</p>}
        {subDescription && <p className="mb-4">{subDescription}</p>}
        <p className="mb-0">
            Если у вас остались вопросы, обращайтесь в службу поддержки:
            <a href="mailto:help@ticketspulse.ru" className="text-black ms-1">
                info@ticketspulse.ru
            </a>
        </p>
    </Card.Body>
);

// Основной компонент отображения данных заказа
const OrderData = () => {
    type StatusKey = 2 | 4 | 5 | 6 | 7;
    const dispatch = useDispatch();
    const [getOrder] = useGetOrderMutation();
    const order = useSelector((state: any) => state.orderTP) as IOrder;
    const [isPolling, setIsPolling] = useState(true);

    // Инициализация заказа из localStorage
    useEffect(() => {
        const orderString = localStorage.getItem("order");
        if (orderString) {
            const parsedOrder = JSON.parse(orderString);
            dispatch(setOrderInStore(parsedOrder));
        }
    }, [dispatch]);

    // Первичный запрос статуса заказа
    useEffect(() => {
        if (order?.id) {
            getOrder(order.id).then((res: any) => {
                if (!res.error) {
                    dispatch(setOrderInStore(res.data));
                }
                if (res.error) {
                    dispatch(setOrderInStore(null as any))
                }
            });
        }
    }, [order?.id, dispatch, getOrder]);

    // Периодическая проверка статуса
    useEffect(() => {
        if (!order?.id || !isPolling) return;

        const interval = setInterval(() => {
            getOrder(order.id).then((res: any) => {
                if (!res.error) {
                    dispatch(setOrderInStore(res.data));
                    if (res.data.orderStatus > 4) {
                        setIsPolling(false); // Остановить опрос при завершении
                    }
                    if (res.error) {
                        dispatch(setOrderInStore(null as any))
                    }
                }
            });
        }, 5000);

        return () => clearInterval(interval);
    }, [order?.id, isPolling, dispatch, getOrder]);

    // Конфигурация статусов
    const statusConfig = {
        2: {
            icon: Lock,
            color: "text-primary",
            title: "Заказ зарезервирован",
            description: "Ошибка оплаты. Мы сохранили Ваш заказ.",
            isLoading: true,
        },
        4: {
            icon: Spinner,
            color: "text-secondary",
            title: "Проверка оплаты",
            description: "Мы проверяем данные об оплате...",
            isLoading: true,
        },
        5: {
            icon: CheckCircle,
            color: "text-success",
            title: "Спасибо!",
            description: "Успешно оплачен!",
            subDescription: "Ваш подарочный сертификат отправлен на E-mail, указанный при оформлении."
        },
        6: {
            icon: ExclamationDiamond,
            color: "text-warning",
            title: "Платеж отклонен"
        },
        7: {
            icon: XCircle,
            color: "text-danger",
            title: "Заказ отменен"
        }
    };

    if (!order || order.orderStatus === null) return null;


    const config = statusConfig[order.orderStatus as StatusKey];
    if (!config) return null;

    return (
        <div className="d-grid align-items-center" style={{ height: '70vh' }}>
            <div className="m-auto" style={{ maxWidth: '768px' }}>
                <Card>
                    <Card.Header className="text-center fs-5">Статус вашего заказа</Card.Header>
                    <StatusMessage {...config} order={order} />
                </Card>
            </div>
        </div>
    );
};

export const CertOrder = () => {
    const [t] = useTranslation("global");

    const dispatch = useDispatch();
    const order = useSelector((state: any) => state.orderTP);
    useEffect(() => {
        const orderString = localStorage.getItem("order");
        if (orderString) {
            try {
                const parsedOrder = JSON.parse(orderString);
                dispatch(setOrderInStore(parsedOrder));
            } catch (error) {
                console.error('Ошибка при парсинге заказа:', error);
            }
        }
    }, [])

    return (
        <>
            <Helmet>
                <title>{t("menu.certificates")} - {t("mainTag.title")}</title>
                <meta name="og:title" content={`${t("menu.certificates")} - ${t("mainTag.title")}`} />
                <meta name="description" content={"Какой подарок можно подарить, чтобы он навсегда остался с человеком? Ответ прост — дарите эмоции! Сертификаты «Ленинград Центра» позволят вам подарить визит на любое шоу в течение 1-го года с момента приобретения."} />
                <meta name="og:description" content={"Какой подарок можно подарить, чтобы он навсегда остался с человеком? Ответ прост — дарите эмоции! Сертификаты «Ленинград Центра» позволят вам подарить визит на любое шоу в течение 1-го года с момента приобретения."} />
                {/* <meta property="og:image" content={data.headLayoutImageType ? `/api/shows/head-wide-img/${data.id})` : "/Show/091A8332.jpg"} /> */}
            </Helmet>
            {order ? (
                // <></>
                <OrderData />
            ) : (
                <div className="d-grid align-items-center" style={{ height: '70vh' }}>
                    <div className="m-auto" style={{ maxWidth: '768px' }}>
                        <Card>
                            <Card.Header className="text-center fs-5">Ошибка данных</Card.Header>
                            <Card.Body className="pt-5 text-center">
                                <div className="text-center text-danger"><XCircle size={60} /></div>
                                <h4>Заказ не найден</h4>
                                <p className="mb-0">
                                    Если у вас есть вопросы, обращайтесь в службу поддержки:
                                    <a href="mailto:help@ticketspulse.ru" className="text-black ms-1">
                                        info@ticketspulse.ru
                                    </a>
                                </p>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            )}

            {/* <CertTitle /> */}
        </>
    )
}